// src/pages/admin/batches/BatchForm.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../api";
import { useNavigate, useParams } from "react-router-dom";
import { ddmmyyyyToIso } from "../../../utils/date";
import PageHeaderSection from "../../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../../components/layout/PageLoadingShell";
import Button from "../../../components/common/Button";
import { TextInput, SelectInput, RadioGroup, DayToggleGroup } from "../../../components/common/form";
import { TimePicker15Min } from "../../../components/common/TimePicker15Min";
import { formatSessionDays, normalizeSessionDaysString, parseSessionDays } from "../../../utils/sessionDays";
import { useRegionalSettings } from "../../../context/RegionalSettingsContext";
import { formatLevelLabel } from "../../../utils/formatLevelLabel";
import { getCarryForwardModeOptions } from "../../../utils/carryForwardCopy";
import { BATCH_LOCK_NOTES, buildBatchUpdateBody, canEditBatchField } from "../../../utils/batchEditRules";
import { coachDisplayName } from '../../../utils/displayNames';
import {
    CHARGES_MODE_LABELS,
    formatChargesMode,
    getPeriodIntervalOptions,
} from "../../../utils/periodBillingCopy";
import { apiErrorMessage } from '../../../utils/apiError';

const MODE = ["ANY", "ONLINE", "OFFLINE"];
const CHARGES_MODE = ["PER_SESSION", "PER_SESSION_HOURLY", "PER_PERIOD"];

const empty = {
    branchId: "",
    levelId: "",
    coachId: "",
    name: "",
    sessionMode: "ANY",
    sessionDays: "",
    defaultStartTod: "",
    defaultEndTod: "",
    chargesMode: "PER_SESSION_HOURLY",
    periodInterval: "MONTHLY",
    periodCharge: "",
    sessionCharge: "",
    carryForwardMode: "NOT_ALLOWED",
};

function normalizeToString(v) {
    if (v === null || v === undefined) return "";
    return String(v);
}

function resolveIdFromPayload(d, idKeys = ["branchId", "branch_id"], nestedKey = "branch", nestedIdKeys = ["id", "branchId"]) {
    // Try top-level keys first
    for (const k of idKeys) {
        if (d[k] != null) return String(d[k]);
    }
    // Try nested object
    if (d[nestedKey]) {
        for (const nk of nestedIdKeys) {
            if (d[nestedKey][nk] != null) return String(d[nestedKey][nk]);
        }
    }
    return "";
}

export default function BatchForm() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { settings } = useRegionalSettings();
    const [form, setForm] = useState(empty);
    const [levels, setLevels] = useState([]);
    const [coaches, setCoaches] = useState([]);
    const [branches, setBranches] = useState([]);
    const [saving, setSaving] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [hasActivity, setHasActivity] = useState(false);
    const [systemManaged, setSystemManaged] = useState(false);
    const [optionsLoadError, setOptionsLoadError] = useState(false);
    const [levelsLoadError, setLevelsLoadError] = useState(false);
    const [loadError, setLoadError] = useState("");

    const isEdit = Boolean(id);
    const locks = { isEdit, hasActivity, systemManaged };

    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
            let branchesResp = null;
            try {
                const u = await API.get("/admin/users", { params: { role: "COACH", active: true, page: 0, size: 200 } });
                if (!mounted) return;
                const list = u?.data?.content ?? u?.data ?? [];
                setCoaches(list);
            } catch (e) {
                console.error("Failed to load coaches", e);
                if (!mounted) return;
                setCoaches([]);
                setOptionsLoadError(true);
            }

            try {
                branchesResp = await API.get("/admin/branches");
                if (!mounted) return;
                const br = branchesResp?.data?.content ?? branchesResp?.data ?? [];
                setBranches(br);
            } catch (e) {
                console.error("Failed to load branches", e);
                if (!mounted) return;
                setBranches([]);
                setOptionsLoadError(true);
            }

            // If editing, load existing batch
            if (id) {
                let batchLoaded = false;
                setLoadError("");
                try {
                    // Try direct GET by id first
                    const res = await API.get(`/admin/batches/${id}`);
                    const d = res?.data;
                    if (d) {
                        // Resolve branch/level/coach IDs defensively
                        let resolvedBranchId = resolveIdFromPayload(d, ["branchId", "branch_id"], "branch", ["id", "branchId"]);
                        if (!resolvedBranchId) {
                            // Attempt to resolve by branch name (if branch id not present)
                            const branchNameCandidates = [
                                d.branchName,
                                d.branch?.name,
                                d.branch?.branchName,
                                d.branch_name,
                                d.branch?.displayName,
                                d.branch?.title,
                            ].filter(Boolean);
                            const payloadBranchName = branchNameCandidates.length ? String(branchNameCandidates[0]).trim().toLowerCase() : "";
                            if (payloadBranchName) {
                                // use branchesResp result if available otherwise fetch branches
                                let branchList = [];
                                try {
                                    branchList = branchesResp ? (branchesResp?.data?.content ?? branchesResp?.data ?? []) : [];
                                    if (!branchList || branchList.length === 0) {
                                        const br = await API.get("/admin/branches");
                                        branchList = br?.data?.content ?? br?.data ?? [];
                                    }
                                } catch (be) {
                                    // ignore
                                }
                                if (branchList && branchList.length) {
                                    const found = branchList.find((x) => {
                                        const nm = ((x.name ?? x.branchName ?? x.displayName ?? "") || "").toString().trim().toLowerCase();
                                        return nm && nm === payloadBranchName;
                                    });
                                    if (found) resolvedBranchId = String(found.id ?? found.branchId ?? found.branch_id ?? "");
                                }
                            }
                        }

                        const resolvedLevelId = normalizeToString(d.levelId ?? d.level_id ?? (d.level && (d.level.levelId ?? d.level.id)) ?? "");
                        const resolvedCoachId = normalizeToString(d.coachId ?? d.coach_id ?? (d.coach && (d.coach.userId ?? d.coach.id)) ?? "");

                        if (!mounted) return;
                        setForm({
                            branchId: resolvedBranchId || "",
                            levelId: resolvedLevelId,
                            coachId: resolvedCoachId,
                            name: d.name ?? "",
                            sessionMode: d.sessionMode ?? d.session_mode ?? "ANY",
                            sessionDays: normalizeSessionDaysString(d.sessionDays ?? d.session_days ?? ""),
                            defaultStartTod: d.defaultStartTod ?? d.default_start_tod ?? "",
                            defaultEndTod: d.defaultEndTod ?? d.default_end_tod ?? "",
                            chargesMode: d.chargesMode ?? d.charges_mode ?? "PER_SESSION_HOURLY",
                            periodInterval: d.periodInterval ?? d.period_interval ?? "MONTHLY",
                            periodCharge: d.periodCharge ?? d.period_charge ?? "",
                            sessionCharge: d.sessionCharge ?? d.session_charge ?? "",
                            carryForwardMode: d.carryForwardMode ?? d.carry_forward_mode ?? d.carryForwardAllowed ?? d.carry_forward_allowed ?? "NOT_ALLOWED",
                        });
                        setHasActivity(!!d.hasActivity);
                        setSystemManaged(!!d.systemManaged);
                        batchLoaded = true;
                    }
                } catch (e) {
                    // If direct GET fails, fallback to list and locate by id
                    console.warn("Direct GET /admin/batches/{id} failed - falling back to list: ", e);
                    try {
                        const listRes = await API.get("/admin/batches", { params: { page: 0, size: 500 } });
                        const items = listRes?.data?.content ?? listRes?.data ?? [];
                        const found = (items || []).find(
                            (it) => String(it.batchId ?? it.id ?? it.batch_id) === String(id) || String(it.id ?? it.batchId ?? it.batch_id) === String(id)
                        );
                        if (found) {
                            const d = found;
                            // resolve branch id similarly from found item
                            let resolvedBranchId = resolveIdFromPayload(d, ["branchId", "branch_id"], "branch", ["id", "branchId"]);
                            if (!resolvedBranchId) {
                                const payloadBranchName = (d.branchName ?? d.branch?.name ?? d.branch?.branchName ?? d.branch_name ?? "").toString().trim().toLowerCase();
                                if (payloadBranchName) {
                                    // use branches list from listRes or previously fetched branches
                                    const branchList = listRes?.data?.content ?? listRes?.data ?? (branchesResp ? (branchesResp?.data?.content ?? branchesResp?.data ?? []) : []);
                                    const fb = (branchList || []).find((x) => {
                                        const nm = ((x.name ?? x.branchName ?? "") || "").toString().trim().toLowerCase();
                                        return nm && nm === payloadBranchName;
                                    });
                                    if (fb) resolvedBranchId = String(fb.id ?? fb.branchId ?? fb.branch_id ?? "");
                                }
                            }

                            const resolvedLevelId = normalizeToString(d.levelId ?? d.level_id ?? (d.level && (d.level.levelId ?? d.level.id)) ?? "");
                            const resolvedCoachId = normalizeToString(d.coachId ?? d.coach_id ?? (d.coach && (d.coach.userId ?? d.coach.id)) ?? "");

                            if (!mounted) return;
                            setForm({
                                branchId: resolvedBranchId || "",
                                levelId: resolvedLevelId,
                                coachId: resolvedCoachId,
                                name: d.name ?? "",
                                sessionMode: d.sessionMode ?? d.session_mode ?? "ANY",
                                sessionDays: normalizeSessionDaysString(d.sessionDays ?? d.session_days ?? ""),
                                defaultStartTod: d.defaultStartTod ?? d.default_start_tod ?? "",
                                defaultEndTod: d.defaultEndTod ?? d.default_end_tod ?? "",
                                chargesMode: d.chargesMode ?? d.charges_mode ?? "PER_SESSION_HOURLY",
                            periodInterval: d.periodInterval ?? d.period_interval ?? "MONTHLY",
                            periodCharge: d.periodCharge ?? d.period_charge ?? "",
                                sessionCharge: d.sessionCharge ?? d.session_charge ?? "",
                                carryForwardMode: d.carryForwardMode ?? d.carry_forward_mode ?? d.carryForwardAllowed ?? d.carry_forward_allowed ?? "NOT_ALLOWED",
                            });
                            setHasActivity(!!d.hasActivity);
                            setSystemManaged(!!d.systemManaged);
                            batchLoaded = true;
                        } else {
                            console.error("Batch not found in list fallback for id", id);
                        }
                    } catch (err) {
                        console.error("Failed to load batch via list fallback", err);
                    }
                }
                if (!batchLoaded && mounted) {
                    setLoadError(t('forms.batch.loadFailed', { defaultValue: 'Could not load this batch.' }));
                }
            }
        } finally {
                if (mounted) setInitialLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [id, t]);

    useEffect(() => {
        if (!form.branchId) {
            setLevels([]);
            return;
        }
        let mounted = true;
        (async () => {
            try {
                const res = await API.get(`/admin/levels/levels/by-branch/${form.branchId}`);
                const list = Array.isArray(res?.data) ? res.data : [];
                const branch = branches.find(
                    (b) => String(b.id ?? b.branchId) === String(form.branchId)
                );
                const branchName = branch?.name ?? branch?.branchName ?? "";
                if (!mounted) return;
                setLevels(
                    list.map((l) => ({
                        ...l,
                        branchName: l.branchName || branchName,
                    }))
                );
                setLevelsLoadError(false);
            } catch (e) {
                console.error("Failed to load levels for branch", e);
                if (mounted) {
                    setLevels([]);
                    setLevelsLoadError(true);
                }
            }
        })();
        return () => {
            mounted = false;
        };
    }, [form.branchId, branches]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => {
            const next = { ...f, [name]: type === "checkbox" ? checked : value };
            if (name === "branchId") {
                next.levelId = "";
            }
            if (name === "chargesMode" && value === "PER_PERIOD") {
                next.carryForwardMode = "NOT_ALLOWED";
            }
            return next;
        });
    };

    const toggleDay = (v) => {
        const set = new Set(parseSessionDays(form.sessionDays));
        if (set.has(v)) set.delete(v);
        else set.add(v);
        setForm((f) => ({ ...f, sessionDays: formatSessionDays([...set]) }));
    };

    const validate = () => {
        const errs = [];
        if (!form.branchId) errs.push(t('forms.batch.validation.selectBranch'));
        if (!form.levelId) errs.push(t('forms.batch.validation.selectLevel'));
        if (!form.coachId) errs.push(t('forms.batch.validation.selectCoach'));
        if (!form.sessionMode) errs.push(t('forms.batch.validation.selectSessionMode'));
        if (!form.name || String(form.name).trim() === "") errs.push(t('forms.batch.validation.enterName'));
        const selectedDays = new Set(parseSessionDays(form.sessionDays));
        if (selectedDays.size === 0) errs.push(t('forms.batch.validation.selectSessionDay'));
        if (!form.defaultStartTod) errs.push(t('forms.batch.validation.selectDefaultStart'));
        if (!form.defaultEndTod) errs.push(t('forms.batch.validation.selectDefaultEnd'));
        if (form.defaultStartTod && form.defaultEndTod && form.defaultEndTod <= form.defaultStartTod) {
            errs.push(t('forms.batch.validation.endAfterStart'));
        }
        return errs;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        // Client-side validations
        const errs = validate();
        if (errs.length) {
            alert(errs.join("\n"));
            setSaving(false);
            return;
        }

        try {
            const body = buildBatchUpdateBody(
                {
                    ...form,
                    sessionDays: formatSessionDays(parseSessionDays(form.sessionDays)),
                },
                { isEdit, hasActivity }
            );
            if (id) {
                await API.put(`/admin/batches/${id}`, body);
            } else {
                await API.post(`/admin/batches`, body);
            }
            navigate("/admin/batches");
        } catch (err) {
            console.error(err);
            const msg = apiErrorMessage(err, t('forms.batch.validation.saveFailed'));
            alert(msg);
        } finally {
            setSaving(false);
        }
    };

    const selected = new Set(parseSessionDays(form.sessionDays));

    if (initialLoading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={id ? t('forms.batch.editTitle') : t('forms.batch.addTitle')}
                subtitle={t('forms.batch.subtitle')}
            />
        );
    }

    if (isEdit && loadError) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    title={t('forms.batch.editTitle')}
                    subtitle={t('forms.batch.subtitle')}
                />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {loadError}
                </div>
                <div className="mt-4">
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/batches')}>
                        {t('common.back', { defaultValue: 'Back' })}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                title={id ? t('forms.batch.editTitle') : t('forms.batch.addTitle')}
                subtitle={t('forms.batch.subtitle')}
            />

            {systemManaged && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    {BATCH_LOCK_NOTES.systemManaged}
                </div>
            )}
            {(optionsLoadError || levelsLoadError) && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.optionsLoadFailed')}
                </div>
            )}

            <form onSubmit={onSubmit} className="section-card grid grid-cols-1 md:grid-cols-3 gap-4">
                <SelectInput
                    label={t('forms.batch.branch')}
                    name="branchId"
                    value={form.branchId}
                    onChange={onChange}
                    required
                    disabled={optionsLoadError || !canEditBatchField('branch', locks)}
                    note={isEdit ? BATCH_LOCK_NOTES.branch : undefined}
                    className={isEdit ? 'disabled:opacity-50 disabled:cursor-not-allowed' : ''}
                >
                    <option value="">{t('forms.common.select')}</option>
                    {branches.map((b) => (
                        <option key={b.id ?? b.branchId} value={b.id ?? b.branchId}>
                            {b.name || b.branchName}
                        </option>
                    ))}
                </SelectInput>

                <SelectInput
                    label={t('forms.batch.level')}
                    name="levelId"
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    value={form.levelId}
                    onChange={onChange}
                    required
                    disabled={!form.branchId || levelsLoadError || !canEditBatchField('level', locks)}
                    note={levelsLoadError ? t('common.optionsLoadFailed') : (isEdit ? BATCH_LOCK_NOTES.level : undefined)}
                >
                    <option value="">{form.branchId ? t('forms.batch.selectEllipsis') : t('forms.batch.selectBranchFirst')}</option>
                    {levels.map((l) => (
                        <option key={l.id ?? l.levelId} value={l.id ?? l.levelId}>
                            {formatLevelLabel(l)}
                        </option>
                    ))}
                </SelectInput>

                <SelectInput
                    label={t('forms.batch.coach')}
                    name="coachId"
                    value={form.coachId}
                    onChange={onChange}
                    required
                    disabled={optionsLoadError || !canEditBatchField('coach', locks)}
                >
                    <option value="">{t('forms.common.select')}</option>
                    {coaches.map((c) => {
                        const nameParts = [c.firstName, c.middleName, c.lastName].filter(Boolean);
                        const fullName = nameParts.length > 0 ? nameParts.join(" ") : c.username || coachDisplayName(null, c.id ?? c.userId, t);
                        return (
                            <option key={c.id ?? c.userId} value={c.id ?? c.userId}>
                                {fullName}
                            </option>
                        );
                    })}
                </SelectInput>

                <TextInput
                    label={t('forms.batch.name')}
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    disabled={!canEditBatchField('name', locks)}
                />

                <SelectInput
                    label={t('forms.batch.sessionMode')}
                    name="sessionMode"
                    value={form.sessionMode}
                    onChange={onChange}
                    required
                    disabled={!canEditBatchField('sessionMode', locks)}
                >
                    {MODE.map((m) => (
                        <option key={m} value={m}>
                            {t(`sessionModes.${m}`, { defaultValue: m })}
                        </option>
                    ))}
                </SelectInput>

                <DayToggleGroup
                    selected={selected}
                    onToggle={toggleDay}
                    required
                    disabled={!canEditBatchField('schedule', locks)}
                />

                <div>
                    <TimePicker15Min
                        value={form.defaultStartTod}
                        onChange={(val) => setForm(f => ({
                            ...f,
                            defaultStartTod: val,
                            defaultEndTod: f.defaultEndTod && val && f.defaultEndTod <= val ? "" : f.defaultEndTod,
                        }))}
                        placeholder={t('forms.batch.selectStartTime')}
                        label={t('forms.batch.defaultStart')}
                        className="w-full"
                        required
                        showDuration={true}
                        otherTime={form.defaultEndTod}
                        maxTime={form.defaultEndTod || null}
                        disabled={!canEditBatchField('schedule', locks)}
                    />
                </div>

                <div>
                    <TimePicker15Min
                        value={form.defaultEndTod}
                        onChange={(val) => setForm(f => ({ ...f, defaultEndTod: val }))}
                        placeholder={t('forms.batch.selectEndTime')}
                        label={t('forms.batch.defaultEnd')}
                        className="w-full"
                        required
                        showDuration={true}
                        otherTime={form.defaultStartTod}
                        minTime={form.defaultStartTod || null}
                        disabled={!canEditBatchField('schedule', locks)}
                    />
                </div>

                {/* Billing Configuration */}
                <div className="md:col-span-3 border-t pt-4">
                    <h3 className="form-section-title">{t('forms.batch.billingConfig')}</h3>
                </div>

                <SelectInput
                    label={t('forms.batch.chargesMode')}
                    name="chargesMode"
                    value={form.chargesMode}
                    onChange={onChange}
                    required
                    disabled={!canEditBatchField('chargesMode', locks)}
                    note={
                        isEdit && hasActivity
                            ? BATCH_LOCK_NOTES.chargesMode
                            : form.chargesMode === "PER_SESSION"
                              ? t('forms.batch.notePerSession')
                            : form.chargesMode === "PER_SESSION_HOURLY"
                                ? t('forms.batch.notePerSessionHourly')
                                : form.chargesMode === "PER_PERIOD"
                                  ? t('forms.batch.notePerPeriod')
                                  : undefined
                    }
                >
                    {CHARGES_MODE.map((m) => (
                        <option key={m} value={m}>
                            {formatChargesMode(m)}
                        </option>
                    ))}
                </SelectInput>

                {(form.chargesMode === "PER_SESSION" || form.chargesMode === "PER_SESSION_HOURLY") && (
                    <TextInput
                        label={t('forms.batch.sessionCharge', { currency: settings?.currencySymbol || settings?.currencyCode || '' })}
                        type="number"
                        name="sessionCharge"
                        value={form.sessionCharge}
                        onChange={onChange}
                        step="0.01"
                        min="0"
                        required
                        disabled={!canEditBatchField('sessionCharge', locks)}
                        note={isEdit ? BATCH_LOCK_NOTES.sessionCharge : undefined}
                    />
                )}

                {form.chargesMode === "PER_PERIOD" && (
                    <>
                        <SelectInput
                            label={t('forms.batch.periodLength')}
                            name="periodInterval"
                            value={form.periodInterval}
                            onChange={onChange}
                            required
                            disabled={!canEditBatchField('chargesMode', locks)}
                        >
                            {getPeriodIntervalOptions().map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </SelectInput>
                        <TextInput
                            label={t('forms.batch.periodFee', { currency: settings?.currencySymbol || settings?.currencyCode || '' })}
                            type="number"
                            name="periodCharge"
                            value={form.periodCharge}
                            onChange={onChange}
                            step="0.01"
                            min="0"
                            required
                            disabled={!canEditBatchField('periodCharge', locks)}
                            note={!canEditBatchField('periodCharge', locks) ? BATCH_LOCK_NOTES.monthlyYearly : undefined}
                        />
                    </>
                )}

                {form.chargesMode !== "PER_PERIOD" && (
                <RadioGroup
                    className="md:col-span-3"
                    label={t('forms.batch.carryForward')}
                    name="carryForwardMode"
                    value={form.carryForwardMode}
                    onChange={onChange}
                    options={getCarryForwardModeOptions()}
                    required
                    disabled={!canEditBatchField('carryForward', locks)}
                    note={!canEditBatchField('carryForward', locks) ? BATCH_LOCK_NOTES.carryForward : undefined}
                />
                )}

                {form.chargesMode === "PER_PERIOD" && (
                    <p className="md:col-span-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                        {t('forms.batch.periodNoCarryForward')}
                    </p>
                )}

                <div className="md:col-span-3 flex items-center justify-end gap-2 mt-2">
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/batches')}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="submit" variant="primary" disabled={saving || systemManaged}>
                        {saving ? t('forms.common.saving') : t('forms.common.save')}
                    </Button>
                </div>
            </form>
        </div>
    );
}